import React, { useMemo, useState } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import { AgCharts } from 'ag-charts-react';
import 'ag-charts-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { useMeQuery } from '../../../services/authContext';
import './Analytics.scss';

ModuleRegistry.registerModules([AllCommunityModule, AllEnterpriseModule]);

const ANALYTICS_QUERY = gql`
  query Analytics($timeRangeDays: Int!, $excludeCustomers: [String!]!) {
    analytics(timeRangeDays: $timeRangeDays, excludeCustomers: $excludeCustomers) {
      outlookTotals { outlookTitle viewCount }
      outlookByCustomer { outlookTitle routeType customer viewCount }
      customerDaily { customer timestamp pageViewCount uniqueSessions avgDurationMin }
      customerSummary { customer pageViewCount sessions uniqueUsers lastSeen }
    }
  }
`;

const TIME_RANGE_OPTIONS = [
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
  { label: 'Last 6 months', value: 180 },
  { label: 'Last 12 months', value: 365 },
];

const GRID_HEIGHT = 360;

function dailySeriesByCustomer(rows) {
  const byTimestamp = new Map();
  const customers = new Set();
  for (const r of rows) {
    customers.add(r.customer);
    const t = r.timestamp.slice(0, 10);
    if (!byTimestamp.has(t)) byTimestamp.set(t, { date: t });
    byTimestamp.get(t)[r.customer] = r.pageViewCount;
  }
  return {
    data: Array.from(byTimestamp.values()).sort((a, b) => a.date.localeCompare(b.date)),
    customers: Array.from(customers).sort(),
  };
}

export default function Analytics() {
  const { isAdmin, isDeveloper, loading: meLoading } = useMeQuery();
  const [timeRangeDays, setTimeRangeDays] = useState(180);
  const [excludeEsai, setExcludeEsai] = useState(false);

  const skip = !isAdmin && !isDeveloper;
  const { data, loading, error } = useQuery(ANALYTICS_QUERY, {
    variables: {
      timeRangeDays,
      excludeCustomers: excludeEsai ? ['ESAI'] : [],
    },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  const dailySeries = useMemo(
    () => dailySeriesByCustomer(data?.analytics?.customerDaily ?? []),
    [data?.analytics?.customerDaily]
  );

  if (meLoading) return <div className="analytics-message">Loading…</div>;
  if (skip) {
    return (
      <div className="analytics-message analytics-error">
        Admin or Developer access required to view analytics.
      </div>
    );
  }

  const a = data?.analytics;

  return (
    <div className="analytics-panel">
      <div className="analytics-header">
        <h3>Engagement Analytics</h3>
        <div className="analytics-controls">
          <label className="analytics-range">
            Time range:
            <select
              value={timeRangeDays}
              onChange={e => setTimeRangeDays(Number(e.target.value))}
            >
              {TIME_RANGE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className="analytics-toggle">
            <input
              type="checkbox"
              checked={excludeEsai}
              onChange={e => setExcludeEsai(e.target.checked)}
            />
            Exclude ESAI
          </label>
        </div>
      </div>

      {error && (
        <div className="analytics-error">
          {error.graphQLErrors?.[0]?.message || error.message || 'Failed to load analytics'}
        </div>
      )}
      {loading && !a && <div className="analytics-message">Loading analytics…</div>}

      {a && (
        <>
          <section className="analytics-section">
            <h4>Usage by Outlook</h4>

            <div className="analytics-card">
              <h5>Total views per outlook</h5>
              <AgCharts
                options={{
                  data: a.outlookTotals,
                  series: [
                    { type: 'bar', xKey: 'outlookTitle', yKey: 'viewCount', yName: 'Views', direction: 'horizontal' },
                  ],
                  axes: [
                    { type: 'category', position: 'left' },
                    { type: 'number', position: 'bottom', title: { text: 'Views' } },
                  ],
                  height: 380,
                }}
              />
            </div>

            <div className="analytics-card">
              <h5>Views by outlook &amp; customer</h5>
              <div className="ag-theme-quartz" style={{ height: GRID_HEIGHT, width: '100%' }}>
                <AgGridReact
                  rowData={a.outlookByCustomer}
                  columnDefs={[
                    { field: 'outlookTitle', headerName: 'Outlook', flex: 2, sortable: true, filter: true },
                    { field: 'routeType', headerName: 'Route', flex: 1, sortable: true, filter: true },
                    { field: 'customer', headerName: 'Customer', flex: 2, sortable: true, filter: true },
                    { field: 'viewCount', headerName: 'Views', flex: 1, sortable: true, sort: 'desc',
                      type: 'numericColumn' },
                  ]}
                  defaultColDef={{ resizable: true }}
                />
              </div>
            </div>
          </section>

          <section className="analytics-section">
            <h4>Usage by Customer</h4>

            <div className="analytics-card">
              <h5>Daily page views per customer</h5>
              <AgCharts
                options={{
                  data: dailySeries.data,
                  series: dailySeries.customers.map(customer => ({
                    type: 'line',
                    xKey: 'date',
                    yKey: customer,
                    yName: customer,
                    marker: { enabled: false },
                  })),
                  axes: [
                    { type: 'category', position: 'bottom' },
                    { type: 'number', position: 'left', title: { text: 'Page views' } },
                  ],
                  legend: { position: 'right' },
                  height: 420,
                }}
              />
            </div>

            <div className="analytics-card">
              <h5>Customer summary</h5>
              <div className="ag-theme-quartz" style={{ height: GRID_HEIGHT, width: '100%' }}>
                <AgGridReact
                  rowData={a.customerSummary}
                  columnDefs={[
                    { field: 'customer', headerName: 'Customer', flex: 2, sortable: true, filter: true },
                    { field: 'pageViewCount', headerName: 'Page views', flex: 1, sortable: true, sort: 'desc',
                      type: 'numericColumn' },
                    { field: 'sessions', headerName: 'Sessions', flex: 1, sortable: true, type: 'numericColumn' },
                    { field: 'uniqueUsers', headerName: 'Unique users', flex: 1, sortable: true, type: 'numericColumn' },
                    { field: 'lastSeen', headerName: 'Last seen', flex: 1, sortable: true,
                      valueFormatter: p => p.value ? new Date(p.value).toLocaleDateString() : '' },
                  ]}
                  defaultColDef={{ resizable: true }}
                />
              </div>
            </div>

          </section>
        </>
      )}
    </div>
  );
}
